/**
 * api.ts — Shared API contracts (task A05).
 *
 * Single source of truth for request/response DTOs. The server parses
 * requests with these Zod schemas; the React client imports the inferred
 * types (and request builders) from here instead of duplicating shapes.
 *
 * Envelopes (TODO.md §1):
 *   success: { "data": ... }
 *   error:   { "error": { "code", "message", "details"? } }
 */
import { z } from 'zod';

// ─── Envelopes ──────────────────────────────────────────────────────────

export const apiSuccessEnvelope = <T extends z.ZodType>(schema: T) =>
  z.object({ data: schema });

export const apiErrorEnvelope = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});

// ─── Primitives & rules ─────────────────────────────────────────────────

/**
 * Username rules (locked): trimmed, 3–32 chars, letters/numbers/_/-.
 * `.trim()` is part of the contract: parsing yields the trimmed value.
 */
export const usernameSchema = z
  .string()
  .trim()
  .min(3, 'Username must be 3-32 characters.')
  .max(32, 'Username must be 3-32 characters.')
  .regex(/^[A-Za-z0-9_.-]+$/, 'Username may only contain letters, numbers, _, -, .');

/** Password rules (locked): 10–128 chars, no composition rules. */
export const passwordSchema = z
  .string()
  .min(10, 'Password must be 10-128 characters.')
  .max(128, 'Password must be 10-128 characters.');

/** Case-insensitive uniqueness key (stored as username_normalized). */
export function normalizeUsername(username: string): string {
  return username.toLowerCase();
}

/** Calendar date YYYY-MM-DD (games.end_date). Never a timestamp. */
export const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be formatted YYYY-MM-DD.')
  .refine(isCalendarDate, 'Date must be a real calendar date.');

export function isCalendarDate(value: string): boolean {
  const [year, month, day] = value.split('-').map(Number);
  const dt = new Date(Date.UTC(year, month - 1, day));
  return (
    dt.getUTCFullYear() === year &&
    dt.getUTCMonth() === month - 1 &&
    dt.getUTCDate() === day
  );
}

export const secretPersonCountSchema = z
  .union([z.literal(1), z.literal(2)])
  .describe('Number of secret persons per player (1 or 2).');

// ─── Enums ──────────────────────────────────────────────────────────────

export const gameStatusSchema = z.enum(['Pending', 'Started', 'Ended']);
export type GameStatus = z.infer<typeof gameStatusSchema>;

export const gameRoleSchema = z.enum(['Creator', 'Player']);
export type GameRole = z.infer<typeof gameRoleSchema>;

// ─── Auth ───────────────────────────────────────────────────────────────

export const userDtoSchema = z.object({
  id: z.number().int().positive(),
  username: z.string(),
});
export type UserDto = z.infer<typeof userDtoSchema>;

export const registerRequestSchema = z.object({
  username: usernameSchema,
  password: passwordSchema,
});
export type RegisterRequest = z.infer<typeof registerRequestSchema>;

export const registerResponseDataSchema = userDtoSchema;
export type RegisterResponseData = z.infer<typeof registerResponseDataSchema>;

export const loginRequestSchema = z.object({
  username: usernameSchema,
  password: passwordSchema,
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const loginResponseDataSchema = z.object({
  user: userDtoSchema,
  csrfToken: z.string().min(1),
});
export type LoginResponseData = z.infer<typeof loginResponseDataSchema>;

/** GET /auth/session returns the same shape as login. */
export const sessionResponseDataSchema = loginResponseDataSchema;
export type SessionResponseData = z.infer<typeof sessionResponseDataSchema>;

// ─── Games ──────────────────────────────────────────────────────────────

/** Game display name (creator-chosen): trimmed, 1–64 chars. */
export const gameNameSchema = z
  .string()
  .trim()
  .min(1, 'Game name is required.')
  .max(64, 'Game name must be 1-64 characters.');

export const gameListItemSchema = z.object({
  id: z.number().int().positive(),
  name: z.string(),
  role: gameRoleSchema,
  status: gameStatusSchema,
  giftBudgetCents: z.number().int().positive(),
  endDate: isoDateSchema,
  secretPersonCount: secretPersonCountSchema,
  createdAt: z.string(),
});
export type GameListItem = z.infer<typeof gameListItemSchema>;

export const listGamesResponseDataSchema = z.object({
  games: z.array(gameListItemSchema),
  activeCreatedGameCount: z.number().int().min(0),
  activeCreatedGameLimit: z.number().int().positive(),
});
export type ListGamesResponseData = z.infer<typeof listGamesResponseDataSchema>;

export const createGameRequestSchema = z.object({
  name: gameNameSchema,
  giftBudgetCents: z.number().int().min(1, 'Gift budget must be positive.'),
  endDate: isoDateSchema,
  secretPersonCount: secretPersonCountSchema,
});
export type CreateGameRequest = z.infer<typeof createGameRequestSchema>;

/** Creating a game returns its list entry (the UI then opens the game). */
export const createGameResponseDataSchema = gameListItemSchema;
export type CreateGameResponseData = z.infer<typeof createGameResponseDataSchema>;

export const joinGameRequestSchema = z.object({
  inviteCode: z.string().trim().min(1).max(64),
});
export type JoinGameRequest = z.infer<typeof joinGameRequestSchema>;

export const playerDtoSchema = z.object({
  userId: z.number().int().positive(),
  username: z.string(),
  isReady: z.boolean(),
});
export type PlayerDto = z.infer<typeof playerDtoSchema>;

export const gamePermissionsSchema = z.object({
  canManageWishlist: z.boolean(),
  canMarkReady: z.boolean(),
  canStartGame: z.boolean(),
  canRevealAssignments: z.boolean(),
});
export type GamePermissions = z.infer<typeof gamePermissionsSchema>;

/**
 * Safe game state for members. This contract must NEVER grow assignment
 * fields — secrecy is enforced by omission here and in the service layer.
 */
export const gameDetailSchema = z.object({
  id: z.number().int().positive(),
  name: z.string(),
  inviteCode: z.string(),
  status: gameStatusSchema,
  giftBudgetCents: z.number().int().positive(),
  endDate: isoDateSchema,
  secretPersonCount: secretPersonCountSchema,
  role: gameRoleSchema,
  players: z.array(playerDtoSchema),
  permissions: gamePermissionsSchema,
});
export type GameDetail = z.infer<typeof gameDetailSchema>;

/** Joining a game returns the full (safe) game state. */
export const joinGameResponseDataSchema = gameDetailSchema;
export type JoinGameResponseData = z.infer<typeof joinGameResponseDataSchema>;

/** POST /games/:id/ready — the updated readiness state of the caller. */
export const readyResponseDataSchema = z.object({
  userId: z.number().int().positive(),
  isReady: z.boolean(),
});
export type ReadyResponseData = z.infer<typeof readyResponseDataSchema>;

/** One recipient of the authenticated user's assignments. */
export const assignmentRecipientSchema = z.object({
  userId: z.number().int().positive(),
  username: z.string(),
});
export type AssignmentRecipient = z.infer<typeof assignmentRecipientSchema>;

export const myAssignmentsResponseDataSchema = z.array(assignmentRecipientSchema);
export type MyAssignmentsResponseData = z.infer<typeof myAssignmentsResponseDataSchema>;

/**
 * Snow overlay (owner-directed secret feature): one pairing
 * (giver -> receiver). This is the ONLY contract that ever carries
 * other players' assignments to a browser — the creator-only
 * GET /games/:id/snow endpoint (fetched when the creator opens the
 * overlay, never preloaded).
 */
export const snowPairSchema = z.object({
  giver: userDtoSchema,
  receiver: userDtoSchema,
});
export type SnowPair = z.infer<typeof snowPairSchema>;

export const snowPairsResponseDataSchema = z.object({
  pairs: z.array(snowPairSchema),
});
export type SnowPairsResponseData = z.infer<typeof snowPairsResponseDataSchema>;

// ─── Wishlists ──────────────────────────────────────────────────────────

export const wishlistItemSchema = z.object({
  id: z.number().int().positive(),
  name: z.string().min(1).max(200),
  url: z
    .string()
    .url()
    .refine((value) => value.startsWith('https://'), 'Only https:// URLs are allowed.')
    .nullable(),
  createdAt: z.string(),
});
export type CreateWishlistRequestInput = {
  name: string;
  url?: string;
};
export type WishlistItem = z.infer<typeof wishlistItemSchema>;

export const createWishlistRequestSchema = z.object({
  name: z.string().trim().min(1, 'Name is required.').max(200, 'Name must be 1-200 characters.'),
  // Optional; when present only https:// is allowed.
  url: z
    .union([
      z.string().url().refine((value) => value.startsWith('https://'), 'Only https:// URLs are allowed.'),
      z.literal(''),
    ])
    .optional()
    .transform((value) => (value === undefined || value === '' ? undefined : value)),
});
export type CreateWishlistRequest = z.infer<typeof createWishlistRequestSchema>;

export const wishlistListResponseDataSchema = z.object({
  items: z.array(wishlistItemSchema),
});
export type WishlistListResponseData = z.infer<typeof wishlistListResponseDataSchema>;

// ─── Client HTTP helper ─────────────────────────────────────────────────

/**
 * Typed fetch wrapper used by the React app. The CSRF token is supplied by
 * the caller (the client never persists session material in storage).
 */
export class ApiClient {
  constructor(private readonly fetchImpl: typeof fetch, private readonly getCsrfToken: () => string | null) {}

  async request<T>(method: 'GET', path: string): Promise<T>;
  async request<T>(method: 'POST' | 'PUT' | 'PATCH' | 'DELETE', path: string, body?: unknown): Promise<T>;
  async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const isMutation = method !== 'GET';
    const headers: Record<string, string> = {};
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (isMutation) {
      const csrf = this.getCsrfToken();
      if (csrf) headers['x-csrf-token'] = csrf;
    }
    const res = await this.fetchImpl(`/api/v1${path}`, {
      method,
      headers,
      credentials: 'same-origin',
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (res.status === 204) {
      return undefined as T;
    }
    const json: unknown = await res.json();
    if (!res.ok) {
      const parsed = apiErrorEnvelope.safeParse(json);
      const message = parsed.success ? parsed.data.error.message : 'Unexpected error response';
      const code = parsed.success ? parsed.data.error.code : 'INTERNAL_ERROR';
      throw new ApiClientError(code, message, res.status);
    }
    // Unwrap the standard { data } envelope so callers receive the DTO directly.
    if (json !== null && typeof json === 'object' && 'data' in (json as Record<string, unknown>)) {
      return (json as { data: T }).data;
    }
    return json as T;
  }
}

export class ApiClientError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}
